import {
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ZoomVideo from "@zoom/videosdk";
import { useEffect, useRef, useState } from "react";
import {
  CardHeader,
  CardHeaderText,
  PreviewRoot,
  Progress,
  TestButton,
  TestContainer,
  TestItem,
} from "../../../../styled/Preview";

let localAudio = ZoomVideo.createLocalAudioTrack();
let allDevices;

const mountDevices = async () => {
  allDevices = await ZoomVideo.getDevices();
  const cameraDevices = allDevices.filter((device) => {
    return device.kind === "videoinput";
  });
  const micDevices = allDevices.filter((device) => {
    return device.kind === "audioinput";
  });
  const speakerDevices = allDevices.filter((device) => {
    return device.kind === "audiooutput";
  });
  return {
    mics: micDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    speakers: speakerDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    cameras: cameraDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
  };
};

const Preview = () => {
  const [micList, setMicList] = useState([]);
  const [speakerList, setSpeakerList] = useState([]);
  const [activeMicrophone, setActiveMicrophone] = useState("");
  const [activeSpeaker, setActiveSpeaker] = useState("");
  const [outputLevel, setOutputLevel] = useState(0);
  const [inputLevel, setInputLevel] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);

  const speakerTesterRef = useRef();
  const microphoneTesterRef = useRef();
  const componenteEstaMontado = useRef(true);

  useEffect(() => {
    return () => {
      componenteEstaMontado.current = false;
    };
  }, []);

  useEffect(() => {
    mountDevices().then((devices) => {
      if (componenteEstaMontado.current) {
        setMicList(devices.mics);
        setSpeakerList(devices.speakers);
        if (devices.speakers.length > 0) {
          setActiveSpeaker(devices.speakers[0].deviceId);
        }
        if (devices.mics.length > 0) {
          setActiveMicrophone(devices.mics[0].deviceId);
        }
      }
    });
  }, []);

  const onTestSpeakerClick = () => {
    if (microphoneTesterRef.current) {
      microphoneTesterRef.current.destroy();
      microphoneTesterRef.current = undefined;
    }
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
      setOutputLevel(0);
    } else {
      speakerTesterRef.current = localAudio.testSpeaker({
        speakerId: activeSpeaker,
        onAnalyseFrequency: (value) => {
          setOutputLevel(Math.min(100, value));
        },
      });
      setIsPlayingAudio(true);
    }
  };

  const onTestMicrophoneClick = () => {
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
    }
    if (!isPlayingRecording && !isRecordingVoice) {
      microphoneTesterRef.current = localAudio.testMicrophone({
        microphoneId: activeMicrophone,
        speakerId: activeSpeaker,
        recordAndPlay: true,
        onAnalyseFrequency: (value) => {
          setInputLevel(Math.min(100, value));
        },
        onStartRecording: () => {
          setIsRecordingVoice(true);
        },
        onStartPlayRecording: () => {
          setIsRecordingVoice(false);
          setIsPlayingRecording(true);
        },
        onStopPlayRecording: () => {
          setIsPlayingRecording(false);
        },
      });
    } else if (isRecordingVoice) {
      microphoneTesterRef.current?.stopRecording();
      setIsRecordingVoice(false);
    } else if (isPlayingRecording) {
      microphoneTesterRef.current?.stop();
      setIsPlayingRecording(false);
    }
  };

  let microphoneBtn = "Probar microfono";
  if (isRecordingVoice) {
    microphoneBtn = "Grabando";
  } else if (isPlayingRecording) {
    microphoneBtn = "Empezando";
  }

  return (
    <PreviewRoot>
      <TestContainer>
        <CardHeader>
          <CardHeaderText variant="h6">Prueba de altavoz</CardHeaderText>
        </CardHeader>
        <TestItem>
          <TestButton
            color="secondary"
            variant="contained"
            onClick={onTestSpeakerClick}>
            {isPlayingAudio ? "Parar prueba" : "Probar altavoz"}
          </TestButton>
          {speakerList.length ? (
            <Select
              disabled
              variant="standard"
              sx={{ margin: 1 }}
              onChange={(e) => setActiveSpeaker(e.target.value)}
              value={activeSpeaker}>
              {speakerList.map((item) => (
                <MenuItem value={item.deviceId} key={item.deviceId}>
                  {item.label.split("-")[1]}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <CircularProgress
              color="secondary"
              size="25px"
              sx={{ margin: 1 }}
            />
          )}
          <Progress>
            <Typography>Nivel de salida</Typography>
            <LinearProgress
              value={outputLevel}
              variant="determinate"
              color="secondary"
            />
          </Progress>
        </TestItem>
      </TestContainer>
      <TestContainer>
        <CardHeader>
          <CardHeaderText variant="h6">Prueba de microfono</CardHeaderText>
        </CardHeader>
        <TestItem>
          <TestButton
            variant="contained"
            color="secondary"
            onClick={onTestMicrophoneClick}>
            {microphoneBtn}
          </TestButton>
          {micList.length ? (
            <Select
              disabled
              variant="standard"
              sx={{ margin: 1 }}
              onChange={(e) => setActiveMicrophone(e.target.value)}
              value={activeMicrophone}>
              {micList.map((item) => (
                <MenuItem value={item.deviceId} key={item.deviceId}>
                  {item.label.split("-")[1]}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <CircularProgress
              color="secondary"
              size="25px"
              sx={{ margin: 1 }}
            />
          )}
          <Progress>
            <Typography>Nivel de entrada</Typography>
            <LinearProgress
              value={inputLevel}
              variant="determinate"
              color="secondary"
            />
          </Progress>
        </TestItem>
      </TestContainer>
    </PreviewRoot>
  );
};

export default Preview;
